/**
 * Decent Scale / Half Decent Scale wire format: pure encoding and decoding, no Web Bluetooth.
 * See docs/decent-scale-protocol.md for the sources and the reasoning behind each choice.
 */

export const DECENT_SERVICE_UUID = '0000fff0-0000-1000-8000-00805f9b34fb';
/** Frames from the scale (weight, status, tare replies, buttons). */
export const DECENT_NOTIFY_CHAR_UUID = '0000fff4-0000-1000-8000-00805f9b34fb';
/** Commands to the scale. */
export const DECENT_WRITE_CHAR_UUID = '000036f5-0000-1000-8000-00805f9b34fb';

/** The documented advertised name (Web Bluetooth name prefixes are case-sensitive). */
export const DECENT_NAME_PREFIX = 'Decent Scale';

export const DECENT_MODEL_BYTE = 0x03;

/** Frame types (byte 1). */
export const DecentFrameType = {
  /** LED on/off, power off, heartbeat (commands); battery / units / firmware (reply). */
  led: 0x0a,
  timer: 0x0b,
  tare: 0x0f,
  weightStable: 0xce,
  weightChanging: 0xca,
  button: 0xaa,
  /** HDS: the scale is switching itself off. */
  powerOffEvent: 0x2a,
  /** HDS: battery voltage (unit undocumented). */
  batteryVoltage: 0x22,
} as const;

/** Case-insensitive, ignoring surrounding spaces. */
export function isDecentScaleName(name: string | null | undefined): boolean {
  return !!name && name.trim().toUpperCase().startsWith(DECENT_NAME_PREFIX.toUpperCase());
}

/** XOR of the first `length` bytes (the checksum covers every byte before it). */
export function decentXor(bytes: ArrayLike<number>, length: number = bytes.length): number {
  let x = 0;
  for (let i = 0; i < length; i++) x ^= (bytes[i] ?? 0) & 0xff;
  return x;
}

/** A 7-byte command: 03, type, four data bytes, XOR of the first six. */
export function encodeCommand(type: number, d1 = 0, d2 = 0, d3 = 0, d4 = 0): Uint8Array<ArrayBuffer> {
  for (const b of [type, d1, d2, d3, d4]) {
    if (!Number.isInteger(b) || b < 0 || b > 0xff) throw new RangeError(`Decent command byte out of range: ${b}`);
  }
  const bytes = new Uint8Array([DECENT_MODEL_BYTE, type, d1, d2, d3, d4, 0]);
  bytes[6] = decentXor(bytes, 6);
  return bytes;
}

/**
 * The last data byte of LED-on and tare: 01 = "this app sends heartbeats", so the Half Decent Scale enforces them.
 * Older scales ignore it.
 */
const HEARTBEAT_AWARE = 0x01;

export const decentCommands = {
  /** Weight and timer display on, grams, heartbeat-aware. Also wakes the stream: the scale waits for a first write. */
  ledOn: () => encodeCommand(DecentFrameType.led, 0x01, 0x01, 0x00, HEARTBEAT_AWARE),
  ledOff: () => encodeCommand(DecentFrameType.led, 0x00, 0x00, 0x00, 0x00),
  /** `counter` (0-255) comes back in the scale's confirmation. */
  tare: (counter: number) => encodeCommand(DecentFrameType.tare, counter & 0xff, 0x00, 0x00, HEARTBEAT_AWARE),
  startTimer: () => encodeCommand(DecentFrameType.timer, 0x03),
  stopTimer: () => encodeCommand(DecentFrameType.timer, 0x00),
  resetTimer: () => encodeCommand(DecentFrameType.timer, 0x02),
  /** Firmware v1.2 and the Half Decent Scale. */
  powerOff: () => encodeCommand(DecentFrameType.led, 0x02),
  /** At least every 5 s, or the Half Decent Scale drops the link (after an app opted in with HEARTBEAT_AWARE). */
  heartbeat: () => encodeCommand(DecentFrameType.led, 0x03, 0xff, 0xff, 0x00),
};

/** Firmware as reported in byte 5 of the status reply. */
export type DecentFirmware = 'v1.0' | 'v1.1' | 'v1.2' | 'unknown';

export function decentFirmware(byte: number): DecentFirmware {
  switch (byte) {
    case 0xfe:
      return 'v1.0';
    case 0x02:
      return 'v1.1';
    case 0x03:
      return 'v1.2';
    default:
      return 'unknown';
  }
}

interface FrameBase {
  /** byte 1 */
  frameType: number;
  /** The last byte equals the XOR of every byte before it. Diagnostics only: frames are used either way. */
  checksumOk: boolean;
}

export interface DecentWeightFrame extends FrameBase {
  type: 'weight';
  weightG: number;
  /** CE (vs CA). The Half Decent Scale has no notion of stable weight; Taretto ignores it. */
  stableFlag: boolean;
  /** 10-byte frames (firmware 1.2+): time since the scale was switched on, in ms. Not the brew timer. */
  uptimeMs: number | null;
}

export interface DecentStatusFrame extends FrameBase {
  type: 'status';
  units: 'g' | 'oz';
  /** 0-100, or null on USB power (FF). */
  batteryPct: number | null;
  usbPowered: boolean;
  firmware: DecentFirmware;
  firmwareByte: number;
}

export interface DecentTareFrame extends FrameBase {
  type: 'tare';
  counter: number;
  /** Byte 5 is FE: the scale confirms the tare. */
  confirmed: boolean;
}

export interface DecentButtonFrame extends FrameBase {
  type: 'button';
  /** 1 = O, 2 = square. */
  button: number;
  press: 'short' | 'long' | 'unknown';
}

export interface DecentOtherFrame extends FrameBase {
  type: 'powerOffEvent' | 'batteryVoltage' | 'unknown';
}

export type DecentFrame = DecentWeightFrame | DecentStatusFrame | DecentTareFrame | DecentButtonFrame | DecentOtherFrame;

export type DecentParseResult = { ok: true; frame: DecentFrame } | { ok: false; reason: 'length' };

/** The smallest frame: 03, type, four data bytes, XOR. */
export const DECENT_FRAME_LENGTH = 7;

/**
 * Decode one notification. Anything shorter than 7 bytes is malformed. The checksum is reported, never enforced
 * (Decent deprecated it on the HDS, and the 10-byte examples in the docs don't verify).
 */
export function parseDecentFrame(bytes: Uint8Array): DecentParseResult {
  if (bytes.length < DECENT_FRAME_LENGTH) return { ok: false, reason: 'length' };
  const frameType = bytes[1]!;
  const checksumOk = decentXor(bytes, bytes.length - 1) === bytes[bytes.length - 1];
  const base = { frameType, checksumOk };
  switch (frameType) {
    case DecentFrameType.weightStable:
    case DecentFrameType.weightChanging: {
      const raw = (bytes[2]! << 8) | bytes[3]!;
      const signed = raw & 0x8000 ? raw - 0x10000 : raw;
      const uptimeMs = bytes.length >= 10 ? bytes[4]! * 60_000 + bytes[5]! * 1000 + bytes[6]! * 100 : null;
      return { ok: true, frame: { ...base, type: 'weight', weightG: signed / 10, stableFlag: frameType === DecentFrameType.weightStable, uptimeMs } };
    }
    case DecentFrameType.led: {
      // The reply to an LED command has 00 in byte 2. Anything else on this type is undocumented: don't read a
      // battery or firmware out of it.
      if (bytes[2] !== 0x00) return { ok: true, frame: { ...base, type: 'unknown' } };
      const battery = bytes[4]!;
      return {
        ok: true,
        frame: {
          ...base,
          type: 'status',
          units: bytes[3] === 0x01 ? 'oz' : 'g',
          batteryPct: battery === 0xff ? null : Math.min(100, battery),
          usbPowered: battery === 0xff,
          firmware: decentFirmware(bytes[5]!),
          firmwareByte: bytes[5]!,
        },
      };
    }
    case DecentFrameType.tare:
      return { ok: true, frame: { ...base, type: 'tare', counter: bytes[2]!, confirmed: bytes[5] === 0xfe } };
    case DecentFrameType.button:
      return { ok: true, frame: { ...base, type: 'button', button: bytes[2]!, press: bytes[3] === 1 ? 'short' : bytes[3] === 2 ? 'long' : 'unknown' } };
    case DecentFrameType.powerOffEvent:
      return { ok: true, frame: { ...base, type: 'powerOffEvent' } };
    case DecentFrameType.batteryVoltage:
      return { ok: true, frame: { ...base, type: 'batteryVoltage' } };
    default:
      return { ok: true, frame: { ...base, type: 'unknown' } };
  }
}

/** "03 ce 00 65 00 00 a8" */
export function decentHex(bytes: ArrayLike<number>): string {
  return Array.from({ length: bytes.length }, (_, i) => ((bytes[i] ?? 0) & 0xff).toString(16).padStart(2, '0')).join(' ');
}

/** "03 CE 00 65 00 00 A8" or "03CE00650000A8" → bytes (test fixtures and docs). */
export function fromHex(hex: string): Uint8Array {
  const clean = hex.replace(/\s+/g, '');
  if (clean.length % 2 !== 0 || /[^0-9a-f]/i.test(clean)) throw new Error(`Bad hex: ${hex}`);
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}
